import { useMemo } from '../../../lib/teact/teact';

import type { ApiBaseCurrency, ApiCurrencyRates, ApiStakingState, ApiTokenWithPrice } from '../../../api/types';
import type { AmountInputToken } from '../../ui/AmountInput';

import { calculateTokenPrice } from '../../../util/calculatePrice';
import {
  getIsActiveStakingState,
  getIsNewStakeAllowed,
} from '../../../util/staking';

interface Options {
  tokenBySlug?: Record<string, ApiTokenWithPrice>;
  states?: ApiStakingState[];
  selectedStakingId?: string;
  isViewMode?: boolean;
  // Keeps active positions of tokens closed for new stakes selectable (e.g. to navigate to them), even when not
  // selected. Only for the info view; the new-stake form must omit it so closed tokens can't start a new stake.
  shouldKeepActiveBlockedStates?: boolean;
  baseCurrency: ApiBaseCurrency;
  currencyRates: ApiCurrencyRates;
}

export function useTokenDropdown({
  tokenBySlug, states, selectedStakingId, isViewMode, shouldKeepActiveBlockedStates,
  baseCurrency, currencyRates,
}: Options) {
  const selectableTokens = useMemo<AmountInputToken[]>(() => {
    if (!tokenBySlug || !states) {
      return [];
    }

    let stakingTokens = getStakingTokens(
      tokenBySlug, states, selectedStakingId, shouldKeepActiveBlockedStates,
    );

    if (isViewMode) {
      stakingTokens = stakingTokens.filter(({ id }) => id === selectedStakingId);
    }

    const result = stakingTokens.map((token) => ({
      ...token,
      price: calculateTokenPrice(token.priceUsd, baseCurrency, currencyRates),
    }));

    return result;
  }, [
    tokenBySlug, states, isViewMode, selectedStakingId, shouldKeepActiveBlockedStates,
    baseCurrency, currencyRates,
  ]);

  const selectedToken = useMemo(
    () => selectableTokens.find((token) => token.id === selectedStakingId),
    [selectableTokens, selectedStakingId],
  );

  return [selectedToken, selectableTokens] as const;
}

export function getStakingTokens(
  tokenBySlug: Record<string, ApiTokenWithPrice>,
  states: ApiStakingState[],
  selectedStakingId?: string,
  shouldKeepActiveBlockedStates?: boolean,
) {
  return states
    .filter((state) => tokenBySlug[state.tokenSlug]
      && (getIsNewStakeAllowed(state.tokenSlug)
        || state.id === selectedStakingId
        || (shouldKeepActiveBlockedStates && getIsActiveStakingState(state))))
    .map<ApiTokenWithPrice & { id: string }>((state) => ({
      ...tokenBySlug[state.tokenSlug],
      id: state.id,
    }));
}
